import React, { useContext, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  CustomInput,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Table
} from "reactstrap";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";

export default injectIntl(function ShopeeConnectionsPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const shops = workspace.shops || [];
  const [wizardModal, setWizardModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Wizard state
  const [importPeriod, setImportPeriod] = useState("30");
  const [sourceOfTruth, setSourceOfTruth] = useState("VMASS");
  const [stockPolicyMode, setStockPolicyMode] = useState("SHARED");
  const [safetyBuffer, setSafetyBuffer] = useState(5);

  return (
    <React.Fragment>
      {/* Header Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1"><IntlMessages id="shopee.workspace.connections.title" /></h5>
          <div className="text-muted small"><IntlMessages id="shopee.workspace.connections.subtitle" /></div>
        </div>
        <Button color="primary" onClick={() => { setWizardStep(1); setWizardModal(true); }}>
          + <IntlMessages id="shopee.workspace.connections.add-shop-wizard" />
        </Button>
      </div>

      {/* Connected Shops Grid */}
      <Row>
        {shops.length ? (
          shops.map(shop => (
            <Col md="6" lg="4" key={shop.shop_id} className="mb-3">
              <Card className="h-100 border-left-primary">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 font-weight-bold">
                      {shop.shop_name || (
                        <IntlMessages
                          id="shopee.workspace.overview.shop-label"
                          values={{ shopId: shop.shop_id }}
                        />
                      )}
                    </h6>
                    <Badge color="success"><IntlMessages id="shopee.workspace.connections.status-active" /></Badge>
                  </div>
                  <div className="text-muted small mb-2">
                    <IntlMessages
                      id="shopee.workspace.connections.region-id"
                      values={{ region: shop.shop_region || "VN", shopId: shop.shop_id }}
                    />
                  </div>

                  <div className="mb-3">
                    <div className="small font-weight-bold mb-1"><IntlMessages id="shopee.workspace.connections.scopes-label" /></div>
                    <div className="d-flex flex-wrap">
                      <Badge color="light" className="border mr-1 mb-1"><IntlMessages id="shopee.workspace.connections.scope.orders" /></Badge>
                      <Badge color="light" className="border mr-1 mb-1"><IntlMessages id="shopee.workspace.connections.scope.catalog" /></Badge>
                      <Badge color="light" className="border mr-1 mb-1"><IntlMessages id="shopee.workspace.connections.scope.stock" /></Badge>
                      <Badge color="light" className="border mr-1 mb-1"><IntlMessages id="shopee.workspace.connections.scope.finance" /></Badge>
                      <Badge color="light" className="border mr-1 mb-1"><IntlMessages id="shopee.workspace.connections.scope.return" /></Badge>
                    </div>
                  </div>

                  <div className="border-top pt-2 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      <IntlMessages id="shopee.workspace.connections.last-sync-colon" />{" "}
                      <IntlMessages id="shopee.workspace.overview.activity.just-now" />
                    </small>
                    <div>
                      <Button
                        size="sm"
                        color="outline-warning"
                        className="mr-1"
                        title={intl.formatMessage({ id: "shopee.workspace.connections.reauth-title" })}
                        onClick={workspace.connectShop}
                        disabled={workspace.connecting}
                      >
                        <IntlMessages id="shopee.workspace.connections.reauth" />
                      </Button>
                      <Button
                        size="sm"
                        color="outline-danger"
                        title={intl.formatMessage({ id: "shopee.workspace.connections.disconnect-title" })}
                        onClick={() =>
                          workspace.runAction(
                            "disconnect-shop",
                            key => ShopeeV2Api.disconnectShop(shop.shop_id, key),
                            intl.formatMessage(
                              { id: "shopee.workspace.connections.disconnected-notice" },
                              { shop: shop.shop_name || shop.shop_id },
                            )
                          )
                        }
                      >
                        <IntlMessages id="shopee.workspace.connections.disconnect-btn" />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))
        ) : (
          <Col xs="12">
            <Alert color="info" className="text-center py-5">
              <p className="mb-3"><IntlMessages id="shopee.workspace.connections.empty" /></p>
              <Button
                color="primary"
                onClick={workspace.connectShop}
                disabled={workspace.connecting}
              >
                {workspace.connecting ? (
                  <IntlMessages id="shopee.workspace.connect-required.connecting" />
                ) : (
                  <IntlMessages id="shopee.workspace.connections.connect-new" />
                )}
              </Button>
            </Alert>
          </Col>
        )}
      </Row>

      {/* 6-Step Onboarding Wizard Modal */}
      <Modal isOpen={wizardModal} toggle={() => setWizardModal(false)} size="lg">
        <ModalHeader toggle={() => setWizardModal(false)}>
          <IntlMessages
            id="shopee.workspace.connections.wizard.title"
            values={{ step: wizardStep }}
          />
        </ModalHeader>
        <ModalBody>
          {/* Progress Bar */}
          <Progress value={(wizardStep / 6) * 100} color="primary" className="mb-4" style={{ height: 8 }} />

          {/* Step 1: Connect OAuth */}
          {wizardStep === 1 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step1-title" /></h5>
              <p className="text-muted">
                <IntlMessages id="shopee.workspace.connections.wizard.step1-desc" />
              </p>
              <Alert color="secondary" className="my-3">
                <strong><IntlMessages id="shopee.workspace.connections.wizard.step1-permissions" /></strong>{" "}
                <IntlMessages id="shopee.workspace.connections.wizard.step1-permissions-detail" />
              </Alert>
              {workspace.connectError ? (
                <Alert color="danger" className="mt-2">
                  {workspace.connectError}
                </Alert>
              ) : null}
              <div className="text-center my-4">
                <Button
                  color="primary"
                  size="lg"
                  onClick={workspace.connectShop}
                  disabled={workspace.connecting}
                >
                  {workspace.connecting ? (
                    <IntlMessages id="shopee.workspace.connect-required.connecting" />
                  ) : (
                    <React.Fragment>
                      <i className="simple-icon-globe mr-2" />
                      <IntlMessages id="shopee.workspace.connections.wizard.open-oauth" />
                    </React.Fragment>
                  )}
                </Button>
              </div>
            </div>
          ) : null}

          {/* Step 2: Import Data Options */}
          {wizardStep === 2 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step2-title" /></h5>
              <p className="text-muted"><IntlMessages id="shopee.workspace.connections.wizard.step2-desc" /></p>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input type="checkbox" defaultChecked />{" "}
                  <IntlMessages id="shopee.workspace.connections.wizard.step2-products" />
                </Label>
              </FormGroup>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input type="checkbox" defaultChecked />{" "}
                  <IntlMessages id="shopee.workspace.connections.wizard.step2-orders" />
                </Label>
              </FormGroup>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input type="checkbox" defaultChecked />{" "}
                  <IntlMessages id="shopee.workspace.connections.wizard.step2-logistics" />
                </Label>
              </FormGroup>
            </div>
          ) : null}

          {/* Step 3: Source of Truth */}
          {wizardStep === 3 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step3-title" /></h5>
              <p className="text-muted"><IntlMessages id="shopee.workspace.connections.wizard.step3-desc" /></p>
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="radio"
                    name="sot"
                    checked={sourceOfTruth === "VMASS"}
                    onChange={() => setSourceOfTruth("VMASS")}
                  />{" "}
                  <strong><IntlMessages id="shopee.workspace.connections.wizard.step3-vmass-strong" /></strong>{" "}
                  <IntlMessages id="shopee.workspace.connections.wizard.step3-vmass-desc" />
                </Label>
              </FormGroup>
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="radio"
                    name="sot"
                    checked={sourceOfTruth === "SHOPEE"}
                    onChange={() => setSourceOfTruth("SHOPEE")}
                  />{" "}
                  <strong><IntlMessages id="shopee.workspace.connections.wizard.step3-shopee-strong" /></strong>{" "}
                  <IntlMessages id="shopee.workspace.connections.wizard.step3-shopee-desc" />
                </Label>
              </FormGroup>
            </div>
          ) : null}

          {/* Step 4: Product Mapping */}
          {wizardStep === 4 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step4-title" /></h5>
              <Alert color="info">
                <IntlMessages id="shopee.workspace.connections.wizard.step4-info" />
              </Alert>
              <p className="text-muted small"><IntlMessages id="shopee.workspace.connections.wizard.step4-note" /></p>
            </div>
          ) : null}

          {/* Step 5: Stock & Price Policy */}
          {wizardStep === 5 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step5-title" /></h5>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="font-weight-bold"><IntlMessages id="shopee.workspace.connections.wizard.allocation-label" /></Label>
                    <Input
                      type="select"
                      value={stockPolicyMode}
                      onChange={e => setStockPolicyMode(e.target.value)}
                    >
                      <option value="SHARED">{intl.formatMessage({ id: "shopee.workspace.connections.wizard.option-shared" })}</option>
                      <option value="ALLOCATED">{intl.formatMessage({ id: "shopee.workspace.connections.wizard.option-allocated" })}</option>
                      <option value="PERCENTAGE">{intl.formatMessage({ id: "shopee.workspace.connections.wizard.option-percentage" })}</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label className="font-weight-bold"><IntlMessages id="shopee.workspace.connections.wizard.safety-buffer-label" /></Label>
                    <Input
                      type="number"
                      value={safetyBuffer}
                      onChange={e => setSafetyBuffer(Number(e.target.value))}
                    />
                    <small className="text-muted"><IntlMessages id="shopee.workspace.connections.wizard.safety-buffer-hint" /></small>
                  </FormGroup>
                </Col>
              </Row>
            </div>
          ) : null}

          {/* Step 6: Verify & Activate */}
          {wizardStep === 6 ? (
            <div>
              <h5><IntlMessages id="shopee.workspace.connections.wizard.step6-title" /></h5>
              <Alert color="success">
                <strong><IntlMessages id="shopee.workspace.connections.wizard.step6-ready-strong" /></strong>{" "}
                <IntlMessages id="shopee.workspace.connections.wizard.step6-ready-detail" />
              </Alert>
              <ul className="list-unstyled">
                <li className="text-success mb-1"><IntlMessages id="shopee.workspace.connections.wizard.check-oauth" /></li>
                <li className="text-success mb-1">
                  <IntlMessages
                    id="shopee.workspace.connections.wizard.check-sot"
                    values={{ value: sourceOfTruth }}
                  />
                </li>
                <li className="text-success mb-1">
                  <IntlMessages
                    id="shopee.workspace.connections.wizard.check-stock-mode"
                    values={{ mode: stockPolicyMode, buffer: safetyBuffer }}
                  />
                </li>
              </ul>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {wizardStep > 1 ? (
            <Button color="secondary" onClick={() => setWizardStep(s => s - 1)}>
              <IntlMessages id="shopee.workspace.common.back" />
            </Button>
          ) : null}
          {wizardStep < 6 ? (
            wizardStep === 1 ? (
              <Button
                color="primary"
                onClick={workspace.connectShop}
                disabled={workspace.connecting}
              >
                {workspace.connecting ? (
                  <IntlMessages id="shopee.workspace.connections.wizard.opening-shopee" />
                ) : (
                  <React.Fragment>
                    <i className="simple-icon-rocket mr-2" />
                    <IntlMessages id="shopee.workspace.connections.wizard.connect-now" />
                  </React.Fragment>
                )}
              </Button>
            ) : (
              <Button color="primary" onClick={() => setWizardStep(s => s + 1)}>
                <IntlMessages id="shopee.workspace.common.next-step" />
              </Button>
            )
          ) : (
            <Button
              color="success"
              onClick={() => {
                workspace.runAction(
                  "activate-shop",
                  key => ShopeeV2Api.remediateIssues(workspace.selectedShopId, "activate_shop", [], key),
                  intl.formatMessage({ id: "shopee.workspace.connections.wizard.activate-done" })
                );
                setWizardModal(false);
              }}
            >
              <React.Fragment>
                <i className="simple-icon-rocket mr-2" />
                <IntlMessages id="shopee.workspace.connections.wizard.activate-now" />
              </React.Fragment>
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
});
